import { ExternalLink } from "lucide-react";
import { Sparkles } from "@/components/ui/sparkles";
import { Logos3 } from "@/components/ui/logos3";

const clients = [
  {
    id: "logo-grow",
    name: "Grow Partner Agency",
    role: "AI Consulting Client",
    description: "Grow Partner Agency",
    quote: `Amazing work! The project was delivered on time, and the team also provided additional features that were outside the original scope of the contract.

GuruTechAI was professional, supportive, and great to work with. I would definitely consider working with GuruTechAI again for other relevant projects.

Thank you, GuruTechAI!`,
    projects: [
      "AI-powered omnichannel lead automation and follow-up engine",
      "AI-powered GMB profile review reply automation for 42 client locations",
    ],
    href: "https://www.instagram.com/growpartner.agency/",
    image: "/grow-partner-logo.svg",
    className: "h-12 w-auto",
  },
  {
    id: "logo-adswise",
    name: "AdsWise Marketing",
    role: "AI Consulting Client",
    description: "AdsWise Marketing logo",
    quote: `GuruTech AI has been an excellent AI consulting partner for AdsWise Marketing. Their team helped us identify practical ways to integrate AI into our marketing workflows, improve operational efficiency, and automate repetitive processes.

They have a strong understanding of AI tools, automation, and business requirements, and always recommend solutions that are practical, scalable, and aligned with our goals. Their guidance has helped us better understand how AI can improve lead management, customer engagement, and overall productivity.

We highly recommend GuruTech AI to any business looking for a reliable and knowledgeable AI consulting partner.`,
    href: "https://www.linkedin.com/company/adswise-marketing/",
    image: "/adswise_marketing_logo.jpeg",
    className: "h-12 w-auto object-contain",
  },
  {
    id: "logo-ss-enterprises",
    name: "SS Enterprises",
    role: "Completed ERP & AI Integration Project",
    description: "SS Enterprises logo",
    summary: "Delivered an AI-powered ERP with WhatsApp-to-CRM automation, bringing core operations into one system and saving the team time on manual data entry.",
    projects: [
      "Labour management",
      "Inventory management",
      "Attendance and work progress tracking",
      "AI chatbot",
      "WhatsApp integration with automatic CRM data capture",
    ],
    image: "/WhatsApp Image 2026-07-14 at 11.17.03 AM.jpeg",
    className: "h-12 w-auto object-contain",
  },
  {
    id: "sujay-chokkasamudram",
    name: "Sujay Chokkasamudram",
    role: "AI Search Dashboard Client",
    quote:
      "Gurutech AI has commendably fulfilled its obligations, delivering work promptly and in accordance with the established agreement. Mr. Suyash has maintained regular communication concerning product development milestones and has consistently satisfied all specified requirements. Furthermore, he provided assistance in the deployment of the product. It has been a privilege to collaborate with this team.",
    projects: ["AI search dashboard"],
  },
];

const TestimonialsSection = () => {
  return (
    <section className="relative overflow-hidden py-24 border-y border-white/10" data-testid="testimonials-section">
      <Sparkles
        density={900}
        size={1.4}
        opacity={0.7}
        className="absolute inset-0"
        color="var(--sparkles-color)"
      />
      <div
        className="absolute inset-0 opacity-20"
        style={{ background: "radial-gradient(circle at top, var(--gradient-color) 0%, transparent 45%)" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-neon-cyan uppercase tracking-[0.2em] text-xs mb-3">Testimonials & Trust</p>
          <h2 className="section-title">Trusted by AI Teams & Growth Leaders</h2>
          <p className="section-subtitle mt-5">
            Real client partnerships delivering practical AI automation, consulting, and custom platforms.
          </p>
        </div>

        <Logos3 heading="Clients & Partners" logos={clients.filter((client) => client.image)} className="py-8" />

        <div className="grid md:grid-cols-2 gap-6 mt-4">
          {clients.map((client) => (
            <article key={client.name} className="glass-card p-6 flex flex-col gap-5">
              <div className="flex items-center gap-4">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white/15 bg-white shadow-lg shadow-electric-violet/10">
                  {client.image ? (
                    <img
                      src={client.image}
                      alt={client.description}
                      className="h-full w-full object-contain p-2"
                    />
                  ) : (
                    <span
                      className="flex h-full w-full items-center justify-center bg-gradient-to-br from-electric-violet to-neon-cyan text-3xl font-bold text-white"
                      aria-hidden="true"
                    >
                      S
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-white font-bold">{client.name}</h3>
                  <p className="text-sm text-muted-foreground">{client.role}</p>
                </div>
              </div>

              {client.summary && (
                <p className="text-sm text-slate-300 leading-relaxed">{client.summary}</p>
              )}

              {client.quote && (
                <p className="whitespace-pre-line text-sm text-slate-300 leading-relaxed">“{client.quote}”</p>
              )}

              {client.projects && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-neon-cyan">
                    Projects delivered
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    {client.projects.map((project) => (
                      <li key={project} className="flex gap-2">
                        <span className="text-electric-violet" aria-hidden="true">•</span>
                        <span>{project}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {client.href && (
                <a
                  href={client.href}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-auto inline-flex items-center gap-2 text-electric-violet hover:text-neon-cyan transition-colors text-sm font-semibold"
                >
                  Visit profile
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;